import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { store } from '../../store/store.js';
import { setRequiresAccreditation } from '../../store/composition/composition.js';
import { translate } from '../../helpers/translate.js';
import { compositionTypes, completionTypes } from '../../store/composition/helpers.js';
import { toMinutes } from '../../helpers/helpers.js';
import '@polymer/paper-toggle-button/paper-toggle-button.js';
import '@sdb-develop/learning-frontend';
import '../shared/sdb-player-dialog.js';

/**
 * Status of a composition: status, mandatory items and accreditation with study minutes.
 *
 * Two layouts:
 * - default: summary columns (status and mandatory items left, accreditation right), as in the
 *   player header;
 * - `rows-layout`: one fact per row, a label on the left and the value on the right, always on
 *   one line: labels never wrap and the bars shrink to fit. For lists such as the results of a
 *   catalog item.
 */
@customElement('sdb-composition-status')
class SdbCompositionStatus extends LitElement {

    @property({ type: Object })
    accessor composition = undefined;

    // White text and light bars, for use on a photo/hero background.
    @property({type: Boolean})
    accessor light = false;

    // List variant: smaller type, bars and spacing.
    @property({type: Boolean})
    accessor small = false;

    // Rows layout: leaves out the accreditation help text.
    @property({type: Boolean})
    accessor compressed = false;

    @property({type: String})
    accessor noSettings = 'showStatus'; // showAll, showStatus. hideAll

    // Shows the status of another user (admin view): no accreditation toggle, no dialog.
    @property({type: Boolean})
    accessor readOnly = false;

    @property({type: Boolean, attribute: 'rows-layout', reflect: true})
    accessor rowsLayout = false;

    @query('#toggle_button')
    accessor toggleButton;

    @query('#accreditation_warning')
    accessor accreditationWarningDialog;

    get openAccreditationDialog() {
        return this.composition.hasAccreditationCompletionSettings
            && this.composition.requiresAccreditation === null
            && this.composition.progress !== 'completed';
    }

    get completionType() {
        return this.composition.progressSettings?.completionType;
    }

    get completionDuration() {
        return this.composition.progressSettings?.minTotalDuration;
    }

    get hasNoSettings() {
        if (this.completionDuration)
            return false;

        return !this.completionType || this.completionType === completionTypes.NO_SETTINGS;
    }

    get isSingleItem() {
        return this.composition.compositionType === compositionTypes.COMPOSITIONTYPE_SINGLEITEM;
    }

    get icons() {
        return {
            started: 'fal fa-hourglass',
            incomplete: 'fal fa-hourglass-start',
            completed: 'fal fa-check',
            passed: 'fal fa-check',
            failed: 'fal fa-exclamation',
        };
    }

    get progress() {
        return this.composition.progress !== 'completed'
            ? this.composition.progress || 'started'
            : !this.composition.success
                ? 'completed'
                : this.composition.success;
    }

    get timeProgress() {
        return Math.min(
            Math.round(100 * this.composition.totalTimeCompleted / this.completionDuration),
            100,
        );
    }

    get mandatoryProgress() {
        return Math.min(
            Math.round(100 * this.composition.totalMandatoryItemsCompleted / this.composition.totalMandatoryItems),
            100,
        );
    }

    get iconSize() {
        return this.small ? 18 : 22;
    }

    static get styles() {
        return css`
            :host {
                --paper-toggle-button-checked-bar-color: var(--btn-primary);
                --paper-toggle-button-checked-button-color: var(--btn-primary);
                --paper-toggle-button-checked-ink-color: var(--btn-primary);
            }

            paper-toggle-button {
                cursor: pointer;
            }

            /* Default layout: summary columns. */

            .status-container, .accreditation-container {
                display: flex;
                flex-direction: row;
                height: 35px;
            }

            .status-container {
                padding-top: 20px;
            }

            .accreditation-container {
                padding-top: 16px;
                padding-bottom: 4px;
            }

            :host([small]) .status-container {
                padding-top: 5px;
            }

            :host([small]) .accreditation-container {
                padding-top: 1px;
                padding-bottom: 4px;
            }

            .status-container sdb-content-icon {
                margin-right: 10px;
            }

            .accreditation-container paper-toggle-button {
                margin-left: 10px;
                margin-right: -40px;
            }

            .toggle-text {
                font-size: 14px;
                line-height: 1.25;
            }

            .secondHeader {
                margin-top: 10px;
                margin-bottom: 3px;
                white-space: nowrap;
            }

            sdb-content-summaryitem::part(headline) {
                white-space: nowrap;
            }

            /* Rows layout. */

            :host([rows-layout]) {
                display: block;
                color: var(--text-dark, #023850);
            }

            :host([rows-layout][light]) {
                color: var(--text-light, #ffffff);
            }

            .rows {
                display: grid;
                grid-template-columns: max-content minmax(0, 1fr);
                column-gap: 40px;
                row-gap: 10px;
                align-items: center;
                padding: 15px;
            }

            :host([small]) .rows {
                column-gap: 28px;
                row-gap: 6px;
                padding: 8px 0;
                font-size: 14px;
            }

            .rows .label,
            .rows .value {
                display: flex;
                align-items: center;
                min-width: 0;
            }

            .rows .label {
                font-weight: 600;
                white-space: nowrap;
            }

            .rows .value {
                gap: 12px;
            }

            .rows .text {
                white-space: nowrap;
            }

            /* Longer sentences (single-item notice) may wrap. */
            .rows .text.wrap {
                min-width: 0;
                white-space: normal;
                overflow-wrap: anywhere;
            }

            .rows .amount {
                white-space: nowrap;
                font-variant-numeric: tabular-nums;
                opacity: .8;
            }

            /* The bar brings its own vertical margins (host and track); drop them, so bar rows
               are as tall as text rows. */
            .rows sdb-content-progressbar {
                flex: 1 1 auto;
                min-width: 40px;
                margin: 0;
            }

            .rows sdb-content-progressbar::part(progressbar) {
                margin: 0;
            }

            .rows .help {
                grid-column: 2;
                margin-top: -4px;
                font-size: 14px;
                opacity: .8;
                overflow-wrap: anywhere;
            }

            :host([small]) .rows .help {
                font-size: 13px;
            }
        `;
    }

    render() {
        if (!this.composition || this._hasNoSettingsMode('hideAll')) {
            return nothing;
        }

        return this.rowsLayout ? this._renderRows() : this._renderColumns();
    }

    // Default layout ------------------------------------------------------------------------

    _renderColumns() {
        if (this._hasNoSettingsMode('showStatus')) {
            return html`
                <sdb-content-summary
                    bgColor="transparent"
                    ?noPadding=${this.small}>
                    ${this._renderStatusSummaryItem()}
                </sdb-content-summary>
            `;
        }

        if (this.isSingleItem) {
            return html`
                <sdb-content-summary
                    bgColor="transparent"
                    ?noPadding=${this.small}
                    equalWidthColumns>
                    ${this._renderStatusSummaryItem()}
                    <sdb-content-summaryitem
                        slot="2"
                        ?darkText=${!this.light}
                        icon="fal fa-circle-info"
                        headline=${translate('information')}>
                        <div class="status-container">
                            <span>${translate('singleItemMessage')}</span>
                        </div>
                    </sdb-content-summaryitem>
                </sdb-content-summary>
            `;
        }

        return html`
            <sdb-content-summary
                bgColor="transparent"
                ?noPadding=${this.small}
                equalWidthColumns>
                ${this._renderStatusSummaryItem(html`
                    <div class="secondHeader">${this._getMandatoryItemsHeadline()}</div>
                    <sdb-content-progressbar
                        progress=${this.mandatoryProgress}
                        value=${this.composition.totalMandatoryItemsCompleted}
                        totalvalue=${this.composition.totalMandatoryItems}
                        ?lightBar=${this.light}
                        ?smallBar=${this.small}>
                    </sdb-content-progressbar>
                `)}

                ${this.completionDuration ? html`
                    <sdb-content-summaryitem
                        slot="2"
                        ?darkText=${!this.light}
                        icon="fal fa-graduation-cap"
                        headline=${this._getAccreditationHeadline()}>
                        <div class="accreditation-container">
                            <div class="toggle-text">
                                ${this._getAccreditationText()}
                            </div>
                            ${this.readOnly ? nothing : html`
                                <paper-toggle-button
                                    id="toggle_button"
                                    ?disabled=${this.composition.progress === 'completed'}
                                    ?checked=${this.composition.requiresAccreditation}
                                    @click=${() => this._toggleAccreditation()}>
                                </paper-toggle-button>
                            `}
                        </div>
                        ${this.composition.requiresAccreditation ? html`
                            <div class="secondHeader">${translate('mandatoryStudyMinutes')}</div>
                            <sdb-content-progressbar
                                progress=${this.timeProgress}
                                value=${toMinutes(this.composition.totalTimeCompleted)}
                                totalvalue=${toMinutes(this.completionDuration)}
                                ?lightBar=${this.light}
                                ?smallBar=${this.small}>
                            </sdb-content-progressbar>
                        ` : nothing}
                    </sdb-content-summaryitem>
                ` : nothing}
            </sdb-content-summary>

            ${this.completionDuration && !this.readOnly ? this._renderAccreditationDialog() : nothing}
        `;
    }

    _renderStatusSummaryItem(extra = nothing) {
        return html`
            <sdb-content-summaryitem
                slot="1"
                ?darkText=${!this.light}
                icon="fal fa-badge"
                headline=${translate('status')}>
                <div class="status-container">
                    <sdb-content-icon
                        class=${this.progress}
                        icon=${this.icons[this.progress]}
                        size=${this.small ? '24' : '32'}>
                    </sdb-content-icon>
                    <div>${translate(`progress_${this.progress}`)}</div>
                </div>
                ${extra}
            </sdb-content-summaryitem>
        `;
    }

    _getAccreditationHeadline() {
        if (this.composition.requiresAccreditation) {
            return translate('status_accreditation_enabled');
        }

        return this.readOnly
            ? translate('status_accreditation_disabled_readonly')
            : translate('status_accreditation_disabled');
    }

    // Rows layout ---------------------------------------------------------------------------

    _renderRows() {
        const statusOnly = this._hasNoSettingsMode('showStatus');
        const showAccreditation = !statusOnly && !this.isSingleItem && !!this.completionDuration;

        return html`
            <div class="rows">
                ${this._renderStatusRow()}
                ${showAccreditation ? this._renderAccreditationRows() : nothing}
                ${statusOnly
                    ? nothing
                    : this.isSingleItem
                        ? this._renderNoticeRow()
                        : this._renderMandatoryItemsRow()}
            </div>

            ${showAccreditation && !this.readOnly ? this._renderAccreditationDialog() : nothing}
        `;
    }

    _renderStatusRow() {
        return html`
            ${this._renderLabel(translate('status'))}
            <div class="value">
                <sdb-content-icon
                    class=${this.progress}
                    icon=${this.icons[this.progress]}
                    size=${this.iconSize}>
                </sdb-content-icon>
                <span class="text">${translate(`progress_${this.progress}`)}</span>
            </div>
        `;
    }

    _renderNoticeRow() {
        return html`
            ${this._renderLabel(translate('information'))}
            <div class="value">
                <span class="text wrap">${translate('singleItemMessage')}</span>
            </div>
        `;
    }

    _renderMandatoryItemsRow() {
        return html`
            ${this._renderLabel(this._getMandatoryItemsHeadline())}
            ${this._renderBar(
                this.mandatoryProgress,
                this.composition.totalMandatoryItemsCompleted,
                this.composition.totalMandatoryItems,
            )}
        `;
    }

    // Accreditation toggle, followed by the help text and the study minutes bar when enabled.
    _renderAccreditationRows() {
        const enabled = this.composition.requiresAccreditation;

        return html`
            ${this._renderLabel(translate('accreditation'))}
            <div class="value">
                ${this.readOnly ? html`
                    <sdb-content-icon
                        icon=${enabled ? 'fal fa-check' : 'fal fa-xmark'}
                        size=${this.iconSize}>
                    </sdb-content-icon>
                ` : html`
                    <paper-toggle-button
                        id="toggle_button"
                        aria-label=${translate('accreditation')}
                        ?disabled=${this.composition.progress === 'completed'}
                        ?checked=${enabled}
                        @click=${() => this._toggleAccreditation()}>
                    </paper-toggle-button>
                `}
                <span class="text">${translate(enabled ? 'accreditation_on' : 'accreditation_off')}</span>
            </div>
            ${enabled && !this.compressed ? html`
                <div class="help">${this._getAccreditationText()}</div>
            ` : nothing}

            ${enabled ? html`
                ${this._renderLabel(translate('mandatoryStudyMinutes'))}
                ${this._renderBar(
                    this.timeProgress,
                    toMinutes(this.composition.totalTimeCompleted),
                    toMinutes(this.completionDuration),
                )}
            ` : nothing}
        `;
    }

    _renderLabel(text) {
        return html`
            <div class="label">
                <span class="text">${text}</span>
            </div>
        `;
    }

    // The value is rendered next to the bar instead of above it (sdb-content-progressbar's own value slot).
    _renderBar(progress, value, totalValue) {
        return html`
            <div class="value">
                <sdb-content-progressbar
                    progress=${progress}
                    showEmptyBar
                    ?lightBar=${this.light}
                    ?smallBar=${this.small}>
                </sdb-content-progressbar>
                <span class="amount">${value} / ${totalValue}</span>
            </div>
        `;
    }

    // Shared --------------------------------------------------------------------------------

    _renderAccreditationDialog() {
        return html`
            <sdb-player-dialog
                type="form"
                id="accreditation_warning"
                width="900"
                heading=${translate('hdr_accreditation_disable')}
                confirmCaption=${translate('caption_accreditation_disable')}
                disableAutoDismiss
                @cancel=${() => {
                    this.toggleButton.checked = true;
                }}
                @confirm=${() => {
                    this._setRequiresAccreditation(false);
                }}
                .bodyRenderer=${() => html`
                    <div>${unsafeHTML(translate('warning_accreditation_disable'))}</div>
                `}>
            </sdb-player-dialog>
        `;
    }

    _getAccreditationText() {
        const minutes = toMinutes(this.completionDuration);

        if (this.readOnly) {
            // Describes the setting of the viewed user, instead of instructing the current user.
            return this.composition.requiresAccreditation
                ? unsafeHTML(translate('msg_accreditation_enabled_readonly', { minutes }))
                : unsafeHTML(translate('msg_accreditation_disabled_readonly', { minutes }));
        }

        if (this.composition.requiresAccreditation) {
            return unsafeHTML(translate('msg_accreditation_enabled', { minutes }));
        }

        return this.composition.progress === 'completed'
            ? unsafeHTML(translate('msg_accreditation_completed'))
            : unsafeHTML(translate('msg_accreditation_disabled', { minutes }));
    }

    _getMandatoryItemsHeadline() {
        switch(this.completionType) {
            case completionTypes.REQUIRED_BLOCKS:
                return translate('mandatoryItems_marked');
            case completionTypes.ANY_BLOCK:
                return translate('mandatoryItems_any');
            case completionTypes.ALL_BLOCKS:
                return translate('mandatoryItems_all');
            case completionTypes.MIN_NUM_BLOCKS:
                return translate('mandatoryItems_min', { num: this.composition.progressSettings.minNumberItems});
            case completionTypes.NO_SETTINGS:
                return this.completionDuration
                    ? translate('mandatoryItems_durationOnly')
                    : translate('mandatoryItems_noSettings');
        }
    }

    _hasNoSettingsMode(mode) {
        return mode === this.noSettings && this.hasNoSettings;
    }

    _toggleAccreditation() {
        if (this.composition.requiresAccreditation) {
            if (this.mandatoryProgress >= 100)
                this.accreditationWarningDialog.open();
            else
                this._setRequiresAccreditation(false);
        }
        else
            this._setRequiresAccreditation(true);
    }

    _setRequiresAccreditation(flag) {
        store.dispatch(setRequiresAccreditation({
            compositionId: this.composition.id,
            flag: flag,
        }));
    }
}
